@if (categoryWithProducts(); as category) {
  <div class="flex h-full flex-col overflow-hidden">
    <div
      class="flex w-full items-center gap-2.5 pb-8"
      (click)="back()"
      [style.color]="textColor() | contentBuilderColor"
    >
      <i class="icon icon-chevron-down rotate-90 text-xs"></i>
      <h1 class="font-accent text-3.5xl font-semibold">{{ category.name }}</h1>
    </div>

    @if (catalogFilters.applied().length) {
      <div class="mb-6 flex items-center gap-4 rounded-2xl bg-primary-50 px-5 py-4">
        <div class="flex flex-1 flex-wrap items-center gap-3">
          @for (filter of catalogFilters.applied(); track filter.kind + ':' + filter.id) {
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-neutral-800 bg-white px-4 py-2 font-base text-lg text-neutral-900"
              (click)="removeCatalogFilter(filter)"
            >
              <span>
                @if (filter.mode === 'exclude') {
                  {{
                    'catalog-filters.without'
                      | translate
                        : {
                            name:
                              filter.kind === 'allergen'
                                ? ('allergens.' + filter.name | translate)
                                : filter.name,
                          }
                  }}
                } @else {
                  {{ filter.name }}
                }
              </span>
              <i
                class="icon icon-cross text-sm"
                aria-hidden="true"
              ></i>
            </button>
          }
        </div>
        <button
          type="button"
          class="shrink-0 font-base text-lg text-neutral-900 underline"
          (click)="clearCatalogFilters()"
        >
          {{ 'filters.delete-all' | translate }}
        </button>
      </div>
    }

    <!-- Subcategories navigation -->
    @if (visibleSubCategoryTabs().length > 0) {
      <div class="mb-5 relative">
        <!-- Mask overlay to hide content behind tabs -->
        @if (visibleSubCategoryTabs().length > 0) {
          <div
            class="pointer-events-none absolute left-0 right-0 z-5"
            [style.top]="'-8px'"
            [style.height.px]="tabsHeight() + 32"
            [style.backgroundColor]="categoriesBackgroundColor() | contentBuilderColor"
          ></div>
        }

        <div
          class="sticky z-10"
          #tabsContainer
        >
          @if (visibleSubCategoryTabs().length > 0) {
            <app-tab-list-atom
              [config]="tabListAtomConfig()"
              [treeTabs]="visibleSubCategoryTabs()"
              [activeItemId]="activeItemId()"
              (activeItemIdChange)="appScrollSpy.scrollToSectionId($event)"
              (tabChange)="onTabClick($event)"
            >
            </app-tab-list-atom>
          }
        </div>
      </div>
    } @else {
      <ng-template #noSubCategories></ng-template>
    }

    <div class="relative flex flex-1 flex-col overflow-hidden">
      <div
        class="flex flex-1 flex-col gap-5 overflow-scroll scrollbar-hide"
        [class.pt-6]="visibleSubCategoryTabs().length > 0"
        #scrollContainer
        appScrollSpy
        [ids]="subCategoriesIds()"
        [offset]="tabsHeight()"
        [scrollContainer]="scrollContainer"
        [activeSection]="activeItemId()"
        (activeSectionChange)="activeItemId.set($event)"
        #appScrollSpy="appScrollSpy"
      >
        <!-- Main category products -->
        @if (visibleCategoryProducts().length) {
          <div
            class="flex flex-col gap-5 mb-16"
            [@staggeredListAnimation]="{
              value: visibleCategoryProducts().length,
              params: { animateEnterElementFromTopToBottom: true },
            }"
          >
            <div
              class="grid gap-5 overflow-y-scroll p-1 scrollbar-hide"
              [attr.data-testid]="'category-product-list' + category.id"
              [style.gridTemplateColumns]="'repeat(' + productCardColumns() + ', minmax(0, 1fr))'"
            >
              @for (product of visibleCategoryProducts(); track product?.id ?? $index) {
                @if (product) {
                  <app-kiosk-product-card-factory
                    [product]="product"
                    [quantity]="product.quantity"
                    (addProduct)="addProductDispatcher(product)"
                    (showProduct)="openProductModal(product)"
                    [textColor]="textColor()"
                    [cardColor]="cardColor()"
                    [priceColor]="priceColor()"
                    [isProductAvailable]="
                      !!product.available && !unAvailableProductById()?.[product.id]
                    "
                    [buttonBackgroundColor]="buttonBackgroundColor()"
                    [unavailableButtonBackground]="unavailableButtonBackground()"
                    [unavailableButtonText]="unavailableButtonText()"
                    [buttonTextColor]="buttonTextColor()"
                  ></app-kiosk-product-card-factory>
                }
              }
            </div>
          </div>
        }

        <!-- Subcategories products with anchors -->
        @if (visibleSubCategoriesWithProducts().length > 0) {
          @for (subCategory of visibleSubCategoriesWithProducts(); track subCategory.id) {
            <div
              [id]="subCategory.id"
              class="flex flex-col gap-4 mb-16"
              [@staggeredListAnimation]="{
                value: subCategory.productsInformation?.length,
                params: { animateEnterElementFromTopToBottom: true },
              }"
            >
              <h2
                class="font-accent text-4xl font-semibold"
                [style.color]="textColor() | contentBuilderColor"
              >
                {{ subCategory.name }}
              </h2>
              <div
                class="grid gap-5 overflow-y-scroll p-1 scrollbar-hide"
                [attr.data-testid]="'subcategory-product-list' + subCategory.id"
                [style.gridTemplateColumns]="'repeat(' + productCardColumns() + ', minmax(0, 1fr))'"
              >
                @for (product of subCategory.productsInformation; track product?.id ?? $index) {
                  @if (product) {
                    <app-kiosk-product-card-factory
                      [product]="product"
                      [quantity]="product.quantity"
                      (addProduct)="addProductDispatcher(product)"
                      (showProduct)="openProductModal(product)"
                      [textColor]="textColor()"
                      [cardColor]="cardColor()"
                      [priceColor]="priceColor()"
                      [isProductAvailable]="
                        !!product.available && !unAvailableProductById()?.[product.id]
                      "
                      [buttonBackgroundColor]="buttonBackgroundColor()"
                      [unavailableButtonBackground]="unavailableButtonBackground()"
                      [unavailableButtonText]="unavailableButtonText()"
                      [buttonTextColor]="buttonTextColor()"
                    ></app-kiosk-product-card-factory>
                  }
                }
              </div>
            </div>
          }
        }
      </div>
    </div>
  </div>
}
